import {
  Badge,
  Checkbox,
  Flex,
  Stack,
  Text,
  VisuallyHidden,
} from '@chakra-ui/react';
import { ImageWithSkeleton } from 'features/common/components/ImageWithSkeleton';
import { AddEditScopedContextValues } from 'features/context-ids/hooks/useAddEditScopedContext';
import { SitecoreContextResourceType } from 'features/context-ids/types/types';
import {
  getResourceDisplayConfig,
  isSensitiveResource,
} from 'features/context-ids/utils';
import { CONTEXT_IDS_TEXT } from 'features/context-ids/constants';
import { SensitiveResourceIcon } from 'features/context-ids/components/SensitiveResourceIcon';
import { useMemo } from 'react';

export const useScopedResourcesColumns = ({
  setValues,
  values,
  scopedContextData,
}: {
  setValues: (values: AddEditScopedContextValues) => any;
  values: AddEditScopedContextValues;
  scopedContextData: SitecoreContextResourceType[];
}) =>
  useMemo(
    () => [
      {
        id: 'selection',
        width: '1%',
        Header: '',
        accessor: (accessor: Partial<SitecoreContextResourceType>) => {
          const id = accessor.id as string;
          const name = accessor.name;
          const type = accessor.type;

          const checkboxId = `scoped-resource-checkbox-${id}`;

          return (
            <Checkbox
              id={checkboxId}
              name={name}
              isChecked={!!values.allowedResourceIds?.includes(id)}
              onChange={(e) => {
                const checked = e.target.checked;

                //@ts-ignore
                setValues((prev) => {
                  const currentIds = prev.allowedResourceIds || [];
                  let newAllowedResourceIds: string[];

                  if (checked) {
                    newAllowedResourceIds = [...currentIds, id];
                    const additionalIds: string[] = [];

                    // If Edge is selected, auto-select Files, Personalize and CDP
                    if (type === 'edge') {
                      const filesResources = scopedContextData.filter(
                        (r) => r.type === 'files',
                      );
                      const personalizeResources = scopedContextData.filter(
                        (r) =>
                          r.type === 'personalize' &&
                          r.tenantProductCode === 'Personalize',
                      );
                      const cdpResources = scopedContextData.filter(
                        (r) =>
                          r.type === 'personalize' &&
                          r.tenantProductCode === 'CDP',
                      );
                      [
                        ...filesResources,
                        ...personalizeResources,
                        ...cdpResources,
                      ].forEach((resource) => {
                        if (!newAllowedResourceIds.includes(resource.id)) {
                          additionalIds.push(resource.id);
                        }
                      });
                    }

                    // If Site Analytics is selected, auto-select Files, Forms, Personalize & CDP
                    if (type === 'site') {
                      const filesResources = scopedContextData.filter(
                        (r) => r.type === 'files',
                      );
                      const formsResources = scopedContextData.filter(
                        (r) => r.type === 'forms',
                      );
                      const personalizeResources = scopedContextData.filter(
                        (r) =>
                          r.type === 'personalize' &&
                          r.tenantProductCode === 'Personalize',
                      );
                      const cdpResources = scopedContextData.filter(
                        (r) =>
                          r.type === 'personalize' &&
                          r.tenantProductCode === 'CDP',
                      );

                      [
                        ...filesResources,
                        ...formsResources,
                        ...personalizeResources,
                        ...cdpResources,
                      ].forEach((resource) => {
                        if (!newAllowedResourceIds.includes(resource.id)) {
                          additionalIds.push(resource.id);
                        }
                      });
                    }

                    newAllowedResourceIds = [
                      ...newAllowedResourceIds,
                      ...additionalIds,
                    ];
                  } else {
                    // Remove the deselected resource
                    newAllowedResourceIds = currentIds.filter(
                      (i: string) => i !== id,
                    );
                  }

                  return {
                    ...prev,
                    allowedResourceIds: newAllowedResourceIds,
                  };
                });
              }}
            >
              <VisuallyHidden>Select {name}</VisuallyHidden>
            </Checkbox>
          );
        },
      },
      {
        id: 'name',
        Header: CONTEXT_IDS_TEXT.RESOURCES_HEADER,
        accessor: ({
          tenantProductCode,
          id,
          name,
          type,
        }: Partial<SitecoreContextResourceType>) => {
          const { iconUrl, name: displayName } =
            getResourceDisplayConfig({ tenantProductCode, id, type }) ?? {};
          const isEdge = tenantProductCode === 'Edge';
          const isSensitive = isSensitiveResource(
            name,
            tenantProductCode,
            type,
          );

          return (
            <Stack direction='row' alignItems='center'>
              <ImageWithSkeleton
                key={displayName}
                width='7'
                height='7'
                src={iconUrl}
                alt={displayName}
                flexShrink={0}
              />
              <Flex alignItems='center' gap={2}>
                <Text>{name}</Text>
                <Badge
                  hidden={type !== 'edge'}
                  textTransform='capitalize'
                  colorScheme={isEdge ? 'success' : 'neutral'}
                >
                  {isEdge
                    ? CONTEXT_IDS_TEXT.LIVE_STATUS
                    : CONTEXT_IDS_TEXT.PREVIEW_STATUS}
                </Badge>
                {isSensitive && (
                  <SensitiveResourceIcon size='sm' iconSize='16px' />
                )}
              </Flex>
            </Stack>
          );
        },
        disableSortBy: true,
        width: '30%',
      },
      {
        id: 'tenant',
        Header: CONTEXT_IDS_TEXT.TENANT_HEADER,
        accessor: 'tenantDisplayName',
        disableSortBy: true,
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [setValues, values.allowedResourceIds?.length, scopedContextData],
  );
